import { HttpClient } from '@angular/common/http';
import { Component, ComponentFactoryResolver, ComponentRef, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CheckboxVm } from '../../../../app/services/property-matrix/models';
import { NotificationService } from '../../../../app/shared/services/notification-service/notification.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import '@maptiler/leaflet-maptilersdk';

// Extend L with markerClusterGroup from the side-effect import
declare module 'leaflet' {
  function markerClusterGroup(options?: any): MarkerClusterGroup;
  interface MarkerClusterGroup extends FeatureGroup {
    addLayers(layers: Layer[]): this;
    clearLayers(): this;
  }
}
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AdvertTypeItemDto, AdvertTypeSubgroupDto, AdvertTypesDto, PropertyFilterDto, RangeDto } from 'src/app/services/property-matrixV2/models';
import { LookupService } from 'src/app/services/property-matrixV2/services';
import { AdvertGeneratorDto } from '../../../../app/services/property-matrixV2/models/advert-generator-dto';
import { AdvertService } from '../../../../app/services/property-matrixV2/services/advert.service';
import { PropertyListingService } from '../../../../app/services/property-matrixV2/services/property-listing.service';
import { PropertyListingDto } from '../../../services/property-matrixV2/models/property-listing-dto';
import { PropertyForSaleCardComponent } from './property-for-sale-card/property-for-sale-card.component';
import { PropertyInfoSearchComponent } from './property-info-search/property-info-search.component';
import { environment } from 'src/environments/environment';
export interface SearchResult {
  name: string;
  type: string; // 'Province' | 'District' | 'Town' | 'Suburb'
  lat: number;
  lon: number;
  bounds?: L.LatLngBoundsExpression;
  zoomLevel?: number;
}

export interface BreadcrumbLevel {
  label: string;
  level: number;
}
const SMOOTH_ANIM_OPTIONS = { duration: 1, easeLinearity: 0.4 };

@Component({
  selector: 'app-property-for-sale-search',
  templateUrl: './property-for-sale-search.component.html',
  styleUrls: ['./property-for-sale-search.component.scss']
})
export class PropertyForSaleSearchComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();
  searchResults: SearchResult[] = [];
  private localSearchIndex: SearchResult[] = [];
  private markerClusterGroup: L.MarkerClusterGroup | null = null;
  private geoJsonCache = new Map<string, any>();
  private dynamicComponentRefs: ComponentRef<any>[] = [];
  propertyMarkers: L.Marker[] = [];
  @Output() pageNumberEvent = new EventEmitter<number>();
  public map!: L.Map;
  mapTilerLayer1: any;
  mapTilerLayer2: any;
  SABoundaryGeoJsonLayer!: L.GeoJSON;
  regionsGeoJson!: L.GeoJSON;
  provincesGeoJSONLayer!: L.GeoJSON;
  districtsGeoJSONLayer!: L.GeoJSON;
  placeholderImage: any;
  mapSearch: string = ''
  activeSuburbId = '';
  suburbs = [];
  polygons = [];
  provincePolygons = [];
  advertTypes: AdvertTypesUi[] = [];
  areaPropertyListings: PropertyListingDto[] = [];
  propertyAdverts: AdvertGeneratorDto[] = [];
  isMapView: boolean = true;
  totalProperties: number = 0;
  currentLevel: number = 4;
  countryBounds: any;
  provinceBounds: any;
  districtBounds: any;
  regionBounds: any;
  activeSearchBounds: L.LatLngBounds | null = null;

  lastSelectedProvinceName: string = "";
  lastSelectedProvinceCode: string = "";
  lastSelectedDistrictName: string = "";
  lastSelectedRegionName: string = "";
  breadcrumbLevels: BreadcrumbLevel[] = [];

  key = 'DkQZbR4Rz2uOhvUj5OvG';
  selectedSector: number | null = null;

  searchFilters: MapFilters[] = [
    { id: 2, expanded: false, name: 'PROPERTY STAGE', subItems: [] },
    { id: 3, expanded: false, name: 'PROPERTY TYPE', subItems: [] },
    { id: 4, expanded: false, name: 'PRICE', subItems: [] },
    { id: 5, expanded: false, name: 'SIZE: LAND PARCEL', subItems: [] },
  ];

  selectedSectorOptions: any;
  sectorOptions: CheckboxVm[] = [
    { id: 1, value: 'Developed', checked: false },
    { id: 2, value: 'Developable', checked: false },
  ];

  @ViewChild('priceTrack') priceTrackRef?: ElementRef<HTMLDivElement>;
  readonly priceSliderMin = environment.priceSliderMin;
  readonly priceSliderMax = environment.priceSliderMax;
  priceMinPct = 6;
  priceMaxPct = 62;
  priceFilterActive = false;
  private draggingPriceHandle: 'min' | 'max' | null = null;

  get priceRangeMin(): number {
    return this.priceSliderMin + (this.priceMinPct / 100) * (this.priceSliderMax - this.priceSliderMin);
  }

  get priceRangeMax(): number {
    return this.priceSliderMin + (this.priceMaxPct / 100) * (this.priceSliderMax - this.priceSliderMin);
  }

  get priceMinLabel(): string {
    return this.formatPrice(this.priceRangeMin);
  }

  get priceMaxLabel(): string {
    return this.priceMaxPct >= 99 ? `Above ${this.formatPrice(this.priceSliderMax)}` : this.formatPrice(this.priceRangeMax);
  }

  formatPrice(value: number): string {
    if (value >= 1000000000) {
      const bn = value / 1000000000;
      return 'R' + (bn >= 10 ? Math.round(bn) : Math.round(bn * 10) / 10) + 'bn';
    }
    const m = value / 1000000;
    return 'R' + (m >= 10 ? Math.round(m) : Math.round(m * 10) / 10) + 'm';
  }

  onPriceHandleDragStart(handle: 'min' | 'max', event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.draggingPriceHandle = handle;
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.draggingPriceHandle) return;
    this.updatePriceHandleFromClientX(event.clientX);
  }

  @HostListener('document:touchmove', ['$event'])
  onDocumentTouchMove(event: TouchEvent): void {
    if (!this.draggingPriceHandle) return;
    this.updatePriceHandleFromClientX(event.touches[0].clientX);
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDocumentDragEnd(): void {
    if (!this.draggingPriceHandle) return;
    this.draggingPriceHandle = null;
    this.priceFilterActive = true;
    this.onFilterCheckboxChange();
  }

  private updatePriceHandleFromClientX(clientX: number): void {
    const track = this.priceTrackRef?.nativeElement;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));

    if (this.draggingPriceHandle === 'min') {
      this.priceMinPct = Math.min(pct, this.priceMaxPct);
    } else {
      this.priceMaxPct = Math.max(pct, this.priceMinPct);
    }
  }

  selectedStandSizeOptions: any;
  standSizeOptions: CheckboxVm[] = [
    { id: 1, value: '0 - 2 500m²', checked: false, min: 0, max: 2500 },
    { id: 2, value: '2 501m² - 5 000 m²', checked: false, min: 2501, max: 5000 },
    { id: 3, value: '5 001m² - 1ha', checked: false, min: 5001, max: 10000 },
    { id: 4, value: '1ha - 3ha', checked: false, min: 10000, max: 30000 },
    { id: 5, value: '3ha - 6ha', checked: false, min: 30000, max: 60000 },
    { id: 6, value: '6ha - 10ha', checked: false, min: 60000, max: 100000 },
    { id: 7, value: '10ha - 20ha ', checked: false, min: 100000, max: 200000 },
    { id: 8, value: '20ha - 40ha', checked: false, min: 200000, max: 400000 },
    { id: 9, value: '40h - 100ha', checked: false, min: 400000, max: 1000000 },
    { id: 10, value: 'Above 100ha', checked: false, min: 1000000, max: null },
  ]

  legendItems = [
    { iconName: 'construction', iconPath: "assets/maps/legend/construction.png", text: 'UNFINISHED/INCOMPLETE STRUCTURES\nThis refers to the sale of property that includes incomplete structures or buildings. The property is sold "as-is", without any finished construction.' },
    { iconName: 'expand', iconPath: "assets/maps/legend/expansion.png", text: 'EXPANSION OPPORTUNITY\nThis refers to a property that offers potential for enlargement or development, either through new structures, the extension of existing ones, or the utilization of available land on the property for future construction.' },
    { iconName: 'inventory_2', iconPath: "assets/maps/legend/whitebox.png", text: 'WHITE BOX\nThis involves the sale of a commercial property where the basic structure is complete, including walls, floors, ceilings, and basic utilities, but the final interior finishes (such as flooring, fixtures, and customizations) need to be added by the buyer.' },
    { iconName: 'home', iconPath: "assets/maps/legend/asset.png", text: 'ASSET SALE\nThis sale includes land and any finished buildings along with equipment and fixtures but excludes any business operations. The transaction focuses on physical assets only, without the transfer of the ongoing business or goodwill.' },
    { iconName: 'sync_alt', iconPath: "assets/maps/legend/goingconcern.png", text: 'GOING CONCERN/TURNKEY\nThis involves the sale of both the commercial property and the operating business, allowing the buyer to continue running the business seamlessly. "Turnkey" emphasizes that the business is ready for immediate operation.' },
    { iconName: 'check_circle', iconPath: "assets/maps/legend/approved.png", text: 'APPROVED RIGHTS\nA property with fully authorised planning permissions from the relevant municipality. The property is ready for construction or development in accordance with the granted rights.' },
    { iconName: 'schedule', iconPath: "assets/maps/legend/pending.png", text: 'PENDING RIGHTS\nPlanning permissions or approvals have been applied for but are still under review by the appropriate authorities. Development or construction on the property is contingent upon receiving the necessary permissions.' },
    { iconName: 'lightbulb', iconPath: "assets/maps/legend/potential.png", text: 'POTENTIAL RIGHTS\nThe property is currently not zoned for a specific type of development but has the possibility of securing planning rights in the future. There is a reasonable opportunity for the property to obtain the necessary approvals.' }
  ];

  provinceAbbreviations: { [key: string]: string } = {
    'Eastern Cape': 'EC',
    'Free State': 'FS',
    'Gauteng': 'GT',
    'KwaZulu-Natal': 'KZN',
    'Limpopo': 'LIM',
    'Mpumalanga': 'MP',
    'North West': 'NW',
    'Northern Cape': 'NC',
    'Western Cape': 'WC'
  };

  createLegendIcon(iconUrl: string): string {
    return `<div class="legend-icon" style="background-image: url('${iconUrl}');
    height: 50px;
    width: 50px;
    background-size: 100%;"></div>`;
  }

  initialCenter: [number, number] = [-28.5, 25];
  initialZoom = 6;

  advertGeneratorDeveloped: AdvertGeneratorDto = {};
  propertyIdFromUrl: string | null = null;

  constructor(
    public http: HttpClient,
    public notificationService: NotificationService,
    public componentFactoryResolver: ComponentFactoryResolver,
    public viewContainerRef: ViewContainerRef,
    public propertyService: PropertyListingService,
    public lookupService: LookupService,
    public advertService: AdvertService,
    public dialog: MatDialog,
    public route: ActivatedRoute,
    private propertyListingService: PropertyListingService
  ) { }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) { resolve(); return; }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(`Failed to load script: ${src}`);
      document.head.appendChild(script);
    });
  }

  async ngOnInit(): Promise<void> {
    this.isMapView = window.innerWidth >= 768;
    this.loadAllPropertyAdvertsForList();

    await this.loadScript('https://cdn.maptiler.com/maptiler-sdk-js/v2.0.3/maptiler-sdk.umd.js');
    await this.loadScript('https://cdn.maptiler.com/leaflet-maptilersdk/v2.0.0/leaflet-maptilersdk.js');
    this.getAdvertTypes();
    this.initMap();
    this.initMarkerClustering();
    this.loadSABoundaryLayer();
    this.loadProvinceLayer();
    this.addLegendControl();
    this.preloadSearchGeoJson();
    this.initSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Destroy dynamic component refs
    for (const ref of this.dynamicComponentRefs) {
      ref.destroy();
    }
    this.dynamicComponentRefs = [];

    // Remove map
    if (this.map) {
      this.map.remove();
    }
  }

  // trackBy functions
  trackByFilterId(index: number, item: MapFilters): number { return item.id; }
  trackBySectorId(index: number, item: CheckboxVm): number { return item.id; }
  trackByStandSizeId(index: number, item: CheckboxVm): number { return item.id; }
  trackByAdvertTypeId(index: number, item: AdvertTypesDto): string | undefined { return item.id; }
  trackBySuburbId(index: number, item: any): any { return item.id; }
  trackByBreadcrumbLevel(index: number, item: BreadcrumbLevel): number { return item.level; }
  trackByAdvertId(index: number, item: AdvertGeneratorDto): string { return item.id ?? index.toString(); }

  toggleMapView(): void {
    this.isMapView = !this.isMapView;
    if (this.isMapView) {
      setTimeout(() => this.map?.invalidateSize(), 0);
    }
  }

  private loadAllPropertyAdvertsForList(): void {
    const emptyFilter: PropertyFilterDto = { priceRanges: [], standSizes: [], sectors: [], advertTypes: [] };
    const developedFilter: PropertyFilterDto = { ...emptyFilter, sectors: ['Developed'] };
    const developableFilter: PropertyFilterDto = { ...emptyFilter, sectors: ['Developable'] };

    forkJoin({
      developed: this.propertyListingService
        .apiV1PropertyListingGetPropertyAdvertsByFilterPost$Response({ body: developedFilter }),
      developable: this.propertyListingService
        .apiV1PropertyListingGetPropertyAdvertsByFilterPost$Response({ body: developableFilter })
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ developed, developable }) => {
        const developedAdverts: AdvertGeneratorDto[] = developed.body;
        const developableAdverts: AdvertGeneratorDto[] = developable.body;

        const allAdverts = [...developedAdverts, ...developableAdverts];
        this.propertyAdverts = allAdverts.filter(
          (advert, index, self) => index === self.findIndex(a => a.id === advert.id)
        );
        this.totalProperties = this.propertyAdverts.length;
      },
      error: (error) => console.error('API error:', error)
    });
  }

  onFilterCheckboxChange() {
    if (this.currentLevel < 2) return;
    const selectedPriceRanges: RangeDto[] = this.priceFilterActive
      ? [{ min: this.priceRangeMin, max: this.priceMaxPct >= 99 ? null : this.priceRangeMax }]
      : [];

    const selectedSizeRanges: RangeDto[] = this.standSizeOptions
      .filter(s => s.checked)
      .map(s => ({ min: s.min, max: s.max }));

    let selectedSectors: string[] = [];
    if (this.selectedSector) {
      const selected = this.sectorOptions.find(s => s.id === this.selectedSector);
      if (selected) {
        selectedSectors.push(selected.value);
      }
    }

    const filter: PropertyFilterDto = {
      priceRanges: selectedPriceRanges,
      standSizes: selectedSizeRanges,
      sectors: selectedSectors,
      advertTypes: this.advertTypes.filter(a => a.isChecked).map(a => a.id),
      advertTypeItems: this.collectCheckedTypeItemIds()
    };

    if (selectedSectors.length > 0) {
      this.propertyListingService
        .apiV1PropertyListingGetPropertyAdvertsByFilterPost$Response({ body: filter })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.clearPropertyMarkers();
            const adverts: AdvertGeneratorDto[] = this.applySearchBoundsFilter(response.body);
            this.totalProperties = adverts.length;
            this.propertyAdverts = adverts;
            this.addPropertyAdvertMarkers(adverts);
          },
          error: (error) => console.error('API error:', error)
        });
    } else {
      const developedFilter: PropertyFilterDto = { ...filter, sectors: ['Developed'] };
      const developableFilter: PropertyFilterDto = { ...filter, sectors: ['Developable'] };

      forkJoin({
        developed: this.propertyListingService
          .apiV1PropertyListingGetPropertyAdvertsByFilterPost$Response({ body: developedFilter }),
        developable: this.propertyListingService
          .apiV1PropertyListingGetPropertyAdvertsByFilterPost$Response({ body: developableFilter })
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: ({ developed, developable }) => {
          this.clearPropertyMarkers();

          const developedAdverts: AdvertGeneratorDto[] = developed.body;
          const developableAdverts: AdvertGeneratorDto[] = developable.body;

          const allAdverts = [...developedAdverts, ...developableAdverts];
          const uniqueAdverts = this.applySearchBoundsFilter(allAdverts.filter(
            (advert, index, self) => index === self.findIndex(a => a.id === advert.id)
          ));

          this.totalProperties = uniqueAdverts.length;
          this.propertyAdverts = uniqueAdverts;

          this.addPropertyAdvertMarkers(uniqueAdverts);
        },
        error: (error) => console.error('API error:', error)
      });
    }
  }

  private applySearchBoundsFilter(adverts: AdvertGeneratorDto[]): AdvertGeneratorDto[] {
    if (!this.activeSearchBounds) return adverts;
    return adverts.filter(a => {
      const lat = Number(a.latitude);
      const lon = Number(a.longitude);
      if (isNaN(lat) || isNaN(lon)) return false;
      return this.activeSearchBounds!.contains(L.latLng(lat, lon));
    });
  }

  private initMarkerClustering(): void {
    try {
      const Lany = L as any;
      console.log('[MC] typeof L.markerClusterGroup:', typeof Lany.markerClusterGroup);
      console.log('[MC] typeof L.MarkerClusterGroup:', typeof Lany.MarkerClusterGroup);
      console.log('[MC] L cluster keys:', Object.keys(L).filter(k => k.toLowerCase().includes('cluster')));

      if (typeof Lany.markerClusterGroup === 'function') {
        this.markerClusterGroup = Lany.markerClusterGroup({
          chunkedLoading: true,
          maxClusterRadius: 60,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          disableClusteringAtZoom: 16
        });
        console.log('[MC] Cluster group created (not yet added to map)');
      } else {
        console.warn('[MC] markerClusterGroup not found on L — clustering disabled');
      }
    } catch (err) {
      console.error('[MC] Failed to init clustering:', err);
      this.markerClusterGroup = null;
    }
  }

  private clearPropertyMarkers(): void {
    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
    } else {
      this.propertyMarkers.forEach(marker => this.map.removeLayer(marker));
    }
    this.propertyMarkers = [];
  }

  toggleSector(id: number) {
    if (this.selectedSector === id) {
      this.selectedSector = null;
    } else {
      this.selectedSector = id;
    }
    this.onFilterCheckboxChange();
  }

  initMap() {
    try {
      const mapElement = document.getElementById('siodMap');
      if (mapElement) mapElement.remove();

      this.map = L.map('pfsMap', {
        zoomControl: true,
        scrollWheelZoom: true,
        zoomSnap: 0.25,
        zoomDelta: 0.5,
        wheelDebounceTime: 80,
        wheelPxPerZoomLevel: 120,
        maxZoom: 18
      }).setView(this.initialCenter, this.initialZoom);

      this.map.doubleClickZoom.disable();
      this.map.zoomControl.setPosition('bottomright');

      this.map.on('zoom', () => {
        this.showOrHideSearchBlock(this.map.getZoom());
      });

      const Lglobal = (window as any).L || L;
      this.mapTilerLayer1 = Lglobal.maptilerLayer({
        apiKey: this.key,
        style: '4a922106-593b-4152-9928-abdd6f734abe'
      }).addTo(this.map);

      this.mapTilerLayer2 = Lglobal.maptilerLayer({
        apiKey: this.key,
        style: 'ef535afc-f1ef-48d8-b88a-24ef487093bb'
      });

      this.setZoomEnabled(false);

      if (!this.propertyIdFromUrl) {
        this.addImageOverlay();
      }

    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  addImageOverlay(): void {
    const imageUrl = 'assets/maps/Charcol.png';
    const imageBounds: L.LatLngBoundsExpression = [[-51, -14], [10, 88]];

    if (this.map) {
      if (this.placeholderImage && this.map.hasLayer(this.placeholderImage)) return;
      this.placeholderImage = L.imageOverlay(imageUrl, imageBounds).addTo(this.map);
    }
  }

  onSearchInput(): void {
    this.searchSubject$.next(this.mapSearch);
  }

  onSearchResultSelected($event: MatAutocompleteSelectedEvent): void {
    const selected = this.searchResults.find(r => r.name === $event.option.value);
    if (!selected) return;

    this.searchResults = [];
    this.navigateToSearchResult(selected);
  }

  private navigateToSearchResult(result: SearchResult): void {
    const point = L.latLng(result.lat, result.lon);

    if (result.type === 'Province') {
      // Just fit bounds to the province — let user click into it
      if (result.bounds) {
        this.activeSearchBounds = result.bounds as L.LatLngBounds;
        this.onFilterCheckboxChange();
        this.map.fitBounds(result.bounds, SMOOTH_ANIM_OPTIONS);
      }
      return;
    }

    // For anything deeper (district, municipality, town, suburb, place):
    // Navigate the layer hierarchy to show the correct context
    const provincesData = this.getCachedGeoJson('assets/maps/geojson/SA_Provinces.geojson');
    const districtsData = this.getCachedGeoJson('assets/maps/geojson/SA_Districts.geojson');
    const regionsData = this.getCachedGeoJson('assets/maps/geojson/Metro regions and Local Municipalities.geojson');

    // Find containing province
    let provinceFeature: any = null;
    if (provincesData) {
      provinceFeature = this.findContainingFeature(provincesData, point);
    }

    // Find containing district
    let districtFeature: any = null;
    if (districtsData) {
      districtFeature = this.findContainingFeature(districtsData, point);
    }

    // Find containing region/municipality
    let regionFeature: any = null;
    if (regionsData) {
      regionFeature = this.findContainingFeature(regionsData, point);
    }

    // Set up province context
    if (provinceFeature) {
      const provinceName = provinceFeature.properties.name;
      const abbrev = this.provinceAbbreviations[provinceName];
      this.lastSelectedProvinceName = provinceName;
      this.lastSelectedProvinceCode = abbrev;
      this.provinceBounds = L.geoJSON(provinceFeature).getBounds().pad(0.01);
    }

    // Set up district context
    if (districtFeature) {
      this.lastSelectedDistrictName = districtFeature.properties.DISTRICT_N;
      this.districtBounds = L.geoJSON(districtFeature).getBounds().pad(0.01);
    }

    // Clear existing layers
    this.clearMapLayers(['provincesGeoJSONLayer', 'SABoundaryGeoJsonLayer', 'districtsGeoJSONLayer', 'regionsGeoJson']);
    if (this.map.hasLayer(this.placeholderImage)) this.map.removeLayer(this.placeholderImage);

    if (result.type === 'District' && result.bounds) {
      // Show districts for the province, zoom to the selected district
      this.currentLevel = 1;
      this.updateZoomLimits(this.currentLevel);
      if (this.lastSelectedProvinceCode) {
        this.loadDistrictLayer(this.lastSelectedProvinceCode);
      }
      this.updateBreadcrumb([
        { label: 'South Africa', level: 1 },
        { label: this.lastSelectedProvinceName, level: 2 }
      ]);
      this.map.fitBounds(result.bounds, SMOOTH_ANIM_OPTIONS);
      this.activeSearchBounds = result.bounds as L.LatLngBounds;
      this.onFilterCheckboxChange();
      return;
    }

    // For municipalities, towns, suburbs, places — go to region level
    if (regionFeature) {
      const regionName = regionFeature.properties?.RegionName || regionFeature.properties?.REGION;
      this.lastSelectedRegionName = regionName || 'Region';
      this.regionBounds = L.geoJSON(regionFeature).getBounds().pad(0.02);
    }

    // Swap to satellite layer
    if (this.mapTilerLayer1 && this.map.hasLayer(this.mapTilerLayer1)) {
      this.map.removeLayer(this.mapTilerLayer1);
    }
    if (this.mapTilerLayer2 && !this.map.hasLayer(this.mapTilerLayer2)) {
      this.map.addLayer(this.mapTilerLayer2);
    }

    this.currentLevel = 4;
    this.setZoomEnabled(true);
    this.updateZoomLimits(this.currentLevel);
    this.activeSearchBounds = result.bounds
      ? (result.bounds as L.LatLngBounds)
      : (this.regionBounds || this.districtBounds || this.provinceBounds || null);
    this.onFilterCheckboxChange();

    this.updateBreadcrumb([
      { label: 'South Africa', level: 1 },
      ...(this.lastSelectedProvinceName ? [{ label: this.lastSelectedProvinceName, level: 2 }] : []),
      ...(this.lastSelectedDistrictName ? [{ label: this.lastSelectedDistrictName, level: 3 }] : []),
      ...(this.lastSelectedRegionName ? [{ label: this.lastSelectedRegionName, level: 4 }] : [])
    ]);

    // Zoom to the selected result
    if (result.bounds) {
      this.map.fitBounds(result.bounds, SMOOTH_ANIM_OPTIONS);
    } else {
      const zoom = result.zoomLevel || 14;
      this.map.flyTo([result.lat, result.lon], zoom, { duration: 1.5 });
    }
  }

  private findContainingFeature(geojsonData: any, point: L.LatLng): any | null {
    if (!geojsonData?.features) return null;
    for (const feature of geojsonData.features) {
      try {
        const layer = L.geoJSON(feature);
        const bounds = layer.getBounds();
        if (bounds.contains(point)) {
          // More precise check: use the actual geometry
          let found = false;
          layer.eachLayer((l: any) => {
            if (found) return;
            if (l.getBounds && l.getBounds().contains(point)) {
              found = true;
            }
          });
          if (found) return feature;
        }
      } catch { }
    }
    return null;
  }

  trackBySearchResult(index: number, item: SearchResult): string {
    return item.name + item.type;
  }

  private preloadSearchGeoJson(): void {
    // Pre-cache districts and regions GeoJSON so search can resolve containing features
    const urls = [
      'assets/maps/geojson/SA_Districts.geojson',
      'assets/maps/geojson/Metro regions and Local Municipalities.geojson'
    ];
    for (const url of urls) {
      if (!this.getCachedGeoJson(url)) {
        this.http.get<any>(url).pipe(takeUntil(this.destroy$)).subscribe({
          next: (data) => this.setCachedGeoJson(url, data),
          error: () => { }
        });
      }
    }
  }

  private initSearch(): void {
    // Build local index from cached GeoJSON (provinces + districts)
    this.buildLocalSearchIndex();

    // Debounced search: local first, then MapTiler geocode for towns/suburbs
    this.searchSubject$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      if (!query || query.length < 2) {
        this.searchResults = [];
        return;
      }
      this.performSearch(query);
    });
  }

  private buildLocalSearchIndex(): void {
    this.localSearchIndex = [];

    // Provinces
    const provinces = this.getCachedGeoJson('assets/maps/geojson/SA_Provinces.geojson');
    if (provinces) {
      for (const feature of provinces.features) {
        const name = feature.properties?.name;
        if (name) {
          const layer = L.geoJSON(feature);
          const bounds = layer.getBounds();
          const center = bounds.getCenter();
          this.localSearchIndex.push({
            name, type: 'Province', lat: center.lat, lon: center.lng, bounds, zoomLevel: 7
          });
        }
      }
    }

    // Districts
    const districts = this.getCachedGeoJson('assets/maps/geojson/SA_Districts.geojson');
    if (districts) {
      for (const feature of districts.features) {
        const name = feature.properties?.DISTRICT_N;
        if (name) {
          const layer = L.geoJSON(feature);
          const bounds = layer.getBounds();
          const center = bounds.getCenter();
          this.localSearchIndex.push({
            name, type: 'District', lat: center.lat, lon: center.lng, bounds, zoomLevel: 9
          });
        }
      }
    }

    // Regions/municipalities
    const regions = this.getCachedGeoJson('assets/maps/geojson/Metro regions and Local Municipalities.geojson');
    if (regions) {
      for (const feature of regions.features) {
        const name = feature.properties?.RegionName || feature.properties?.REGION;
        if (name) {
          const layer = L.geoJSON(feature);
          const bounds = layer.getBounds();
          const center = bounds.getCenter();
          this.localSearchIndex.push({
            name, type: 'Municipality', lat: center.lat, lon: center.lng, bounds, zoomLevel: 11
          });
        }
      }
    }
  }

  private performSearch(query: string): void {
    const q = query.toLowerCase();

    // Local matches (provinces, districts, municipalities)
    const localMatches = this.localSearchIndex
      .filter(r => r.name.toLowerCase().includes(q))
      .slice(0, 5);

    // MapTiler geocode for towns/suburbs (SA-bounded)
    const geocodeUrl = `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?key=${this.key}&country=za&limit=5`;

    this.http.get<any>(geocodeUrl).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const apiResults: SearchResult[] = (response.features || []).map((f: any) => ({
          name: f.place_name || f.text,
          type: f.place_type?.[0] || 'Place',
          lat: f.center?.[1],
          lon: f.center?.[0],
          zoomLevel: 14
        }));

        // Combine: local first, then API results, deduplicated
        const combined = [...localMatches];
        for (const r of apiResults) {
          if (!combined.some(c => c.name.toLowerCase() === r.name.toLowerCase())) {
            combined.push(r);
          }
        }
        this.searchResults = combined.slice(0, 8);
      },
      error: () => {
        // Fallback to local only
        this.searchResults = localMatches;
      }
    });
  }

  private getCachedGeoJson(url: string): any | null {
    return this.geoJsonCache.get(url) || null;
  }

  private setCachedGeoJson(url: string, data: any): void {
    this.geoJsonCache.set(url, data);
    // Rebuild search index when new GeoJSON is cached
    this.buildLocalSearchIndex();
  }

  loadSABoundaryLayer(): void {
    const url = 'assets/maps/geojson/SA_Boundary.geojson';
    const cached = this.getCachedGeoJson(url);
    if (cached) {
      this.SABoundaryGeoJsonLayer = L.geoJSON(cached, {
        style: this.transparentSaBoundary
      }).addTo(this.map);
      return;
    }

    this.http.get<any>(url).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.setCachedGeoJson(url, data);
        this.SABoundaryGeoJsonLayer = L.geoJSON(data, {
          style: this.transparentSaBoundary
        }).addTo(this.map);
      },
      error: (error) => console.error('Error loading GeoJSON:', error)
    });
  }

  updateBreadcrumb(levels: BreadcrumbLevel[]): void {
    this.breadcrumbLevels = levels;
  }

  onBreadcrumbClick(level: BreadcrumbLevel): void {
    switch (level.level) {
      case 1:
        this.currentLevel = 0;
        this.activeSearchBounds = null;
        this.map.setMinZoom(1);
        this.map.setMaxZoom(18);
        this.clearMapLayers(['districtsGeoJSONLayer', 'regionsGeoJson']);

        if (this.mapTilerLayer2) {
          if (this.map.hasLayer(this.mapTilerLayer2)) {
            this.map.removeLayer(this.mapTilerLayer2);
          }
          if (this.mapTilerLayer1) {
            if (!this.map.hasLayer(this.mapTilerLayer1)) {
              this.map.addLayer(this.mapTilerLayer1);
            }
          }
        }

        if (!this.map.hasLayer(this.SABoundaryGeoJsonLayer)) {
          this.loadSABoundaryLayer();
        }

        if (!this.map.hasLayer(this.provincesGeoJSONLayer)) {
          this.loadProvinceLayer();
        }

        this.map.setView(this.initialCenter, this.initialZoom);
        this.setZoomEnabled(false);
        this.hideMarkerLayer();
        this.clearPropertyMarkers();

        this.updateBreadcrumb([{ label: 'South Africa', level: 1 }]);
        this.addImageOverlay();
        break;

      case 2:
        this.currentLevel = 1;
        this.updateZoomLimits(this.currentLevel);
        this.clearMapLayers(['districtsGeoJSONLayer', 'regionsGeoJson']);
        this.loadDistrictLayer(this.lastSelectedProvinceCode);

        if (!this.provinceBounds && this.provincesGeoJSONLayer) {
          this.provinceBounds = this.provincesGeoJSONLayer.getBounds();
        }

        if (this.provinceBounds) {
          this.map.fitBounds(this.provinceBounds, SMOOTH_ANIM_OPTIONS);
        }

        if (this.mapTilerLayer2) {
          if (this.map.hasLayer(this.mapTilerLayer2)) {
            this.map.removeLayer(this.mapTilerLayer2);
          }
          if (this.mapTilerLayer1) {
            if (!this.map.hasLayer(this.mapTilerLayer1)) {
              this.map.addLayer(this.mapTilerLayer1);
            }
          }
        }

        this.hideMarkerLayer();
        this.clearPropertyMarkers();
        this.updateBreadcrumb([
          { label: 'South Africa', level: 1 },
          { label: this.lastSelectedProvinceName, level: 2 }
        ]);
        this.addImageOverlay();
        break;

      case 3:
        this.currentLevel = 2;
        this.updateZoomLimits(this.currentLevel);

        this.clearMapLayers(['regionsGeoJson']);
        this.loadregionsGeoJson();
        this.onFilterCheckboxChange();

        if (!this.districtBounds && this.districtsGeoJSONLayer) {
          this.districtBounds = this.districtsGeoJSONLayer.getBounds();
        }

        if (this.districtBounds) {
          this.map.fitBounds(this.districtBounds, SMOOTH_ANIM_OPTIONS);
        }


        this.updateBreadcrumb([
          { label: 'South Africa', level: 1 },
          { label: this.lastSelectedProvinceName, level: 2 },
          { label: this.lastSelectedDistrictName, level: 3 }
        ]);
        break;

      case 4:
        this.loadregionsGeoJson();

        if (!this.regionBounds && this.regionsGeoJson) {
          this.regionBounds = this.regionsGeoJson.getBounds();
        }

        if (this.regionBounds) {
          this.map.fitBounds(this.regionBounds, SMOOTH_ANIM_OPTIONS);
        }

        this.currentLevel = 3;
        this.updateZoomLimits(this.currentLevel);

        this.updateBreadcrumb([
          { label: 'South Africa', level: 1 },
          { label: this.lastSelectedProvinceName, level: 2 },
          { label: this.lastSelectedDistrictName, level: 3 },
          { label: this.lastSelectedRegionName, level: 4 }
        ]);
        break;
    }
  }

  private clearMapLayers(layerNames: string[]): void {
    for (const name of layerNames) {
      const layer = this[name] as L.Layer;
      if (layer && this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    }
  }

  updateZoomLimits(level: number): void {
    const zoomLevels = {
      0: { minZoom: 5, maxZoom: 7 },
      1: { minZoom: 6, maxZoom: 8 },
      2: { minZoom: 9, maxZoom: 11 },
      3: { minZoom: 9, maxZoom: 14 },
      4: { minZoom: 10, maxZoom: 18 },
      default: { minZoom: 5, maxZoom: 18 },
    };

    const { minZoom, maxZoom } = zoomLevels[level] || zoomLevels.default;

    if (this.map) {
      this.map.setMinZoom(minZoom);
      this.map.setMaxZoom(maxZoom);
    }
  }

  onEachFeatureProvinces(feature: any, layer: L.Layer): void {
    const provinceName = feature.properties.name;

    layer.bindTooltip(provinceName, {
      permanent: true,
      direction: 'center',
      className: 'province-label',
      offset: [0, 10]
    });

    layer.on('mouseover', () => {
      const tooltip = layer.getTooltip().getElement();
      tooltip.classList.add('hovered');
      (layer as L.Path).setStyle({ fillColor: '#e8e8e8', fillOpacity: 0.5 });
    });

    layer.on('mouseout', () => {
      const tooltip = layer.getTooltip().getElement();
      tooltip.classList.remove('hovered');
      (layer as L.Path).setStyle({ fillColor: 'transparent', fillOpacity: 1 });
    });

    layer.on({
      click: (e: L.LeafletMouseEvent) => {
        this.provinceBounds = e.target.getBounds().pad(0.01);
        this.currentLevel = 1;
        this.setZoomEnabled(true);
        const clickedLayer = e.target;
        const abbrev = this.provinceAbbreviations[provinceName];

        this.lastSelectedProvinceName = provinceName;
        this.lastSelectedProvinceCode = abbrev;

        this.updateBreadcrumb([
          { label: 'South Africa', level: 1 },
          { label: provinceName, level: 2 }
        ]);

        const paddedBounds = clickedLayer.getBounds().pad(0.01);

        // Clear any stale maxZoom left by previous breadcrumb navigation
        // so flyToBounds can zoom to the correct level for any province size
        this.map.setMaxZoom(18);

        this.map.once('moveend', () => {
          if (this.provincesGeoJSONLayer) {
            this.map.removeLayer(this.provincesGeoJSONLayer);
          }

          if (this.SABoundaryGeoJsonLayer) {
            this.map.removeLayer(this.SABoundaryGeoJsonLayer);
          }
          this.loadDistrictLayer(abbrev);
        });

        this.map.flyToBounds(paddedBounds, {
          duration: 1,
          easeLinearity: 0.4,
          padding: [15, 15]
        });
      }
    });
  }

  onEachFeatureRegions(feature: any, layer: L.Layer): void {
    let regionName = feature.properties?.RegionName || feature.properties?.REGION;

    if (
      feature.geometry &&
      feature.geometry.type &&
      feature.geometry.coordinates &&
      feature.geometry.coordinates.length > 0 &&
      regionName
    ) {
      (layer as L.Path).bindTooltip(regionName, {
        permanent: false,
        direction: 'center',
        className: 'region-label'
      });
    }

    layer.on({
      mouseover: this.highlightRegion.bind(this),
      mouseout: this.resetHighlightRegion.bind(this),
      click: (e: L.LeafletMouseEvent) => {

        this.regionBounds = e.target.getBounds().pad(0.02);
        this.currentLevel = 4;

        this.updateBreadcrumb([
          { label: 'South Africa', level: 1 },
          { label: this.lastSelectedProvinceName, level: 2 },
          { label: this.lastSelectedDistrictName, level: 3 },
          { label: regionName || 'Region', level: 4 }
        ]);

        const bounds = e.target.getBounds().pad(0.02);
        this.map.fitBounds(bounds, SMOOTH_ANIM_OPTIONS);

        if (this.regionsGeoJson) {
          this.map.removeLayer(this.regionsGeoJson);
        }

        // Swap out map tiler layers
        if (this.mapTilerLayer1) {
          if (this.map.hasLayer(this.mapTilerLayer1)) {
            this.map.removeLayer(this.mapTilerLayer1);
          }
          if (this.mapTilerLayer2) {
            if (!this.map.hasLayer(this.mapTilerLayer2)) {
              this.map.addLayer(this.mapTilerLayer2);
            }
          }
        }

        this.updateZoomLimits(this.currentLevel);

        this.map.scrollWheelZoom.enable();
        this.map.boxZoom.enable();
        this.map.keyboard.enable();
        this.map.dragging.enable();
      }
    });
  }

  highlightRegion(e: L.LeafletMouseEvent): void {
    const layer = e.target;
    layer.setStyle({
      color: "#FA8263",
      weight: 3,
      opacity: 0.7,
      fillColor: "#FA8263",
      fillOpacity: 0.5
    });
  }

  resetHighlightRegion(e: L.LeafletMouseEvent): void {
    if (this.regionsGeoJson) {
      this.regionsGeoJson.resetStyle(e.target);
    }
  }

  loadregionsGeoJson(): void {
    const url = 'assets/maps/geojson/Metro regions and Local Municipalities.geojson';
    const cached = this.getCachedGeoJson(url);
    if (cached) {
      this.regionsGeoJson = L.geoJSON(cached, {
        style: this.regionStyle,
        onEachFeature: this.onEachFeatureRegions.bind(this)
      }).addTo(this.map);
      return;
    }

    this.http.get<any>(url).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.setCachedGeoJson(url, data);
        this.regionsGeoJson = L.geoJSON(data, {
          style: this.regionStyle,
          onEachFeature: this.onEachFeatureRegions.bind(this)
        }).addTo(this.map);
      },
      error: (error) => console.error('Error loading GeoJSON:', error)
    });
  }

  loadProvinceLayer(): void {
    const url = 'assets/maps/geojson/SA_Provinces.geojson';
    const cached = this.getCachedGeoJson(url);
    if (cached) {
      this.provincesGeoJSONLayer = L.geoJSON(cached, {
        style: {
          color: 'white',
          weight: 1,
          fillColor: 'transparent',
          fillOpacity: 1
        },
        onEachFeature: this.onEachFeatureProvinces.bind(this)
      }).addTo(this.map);
      return;
    }

    this.http.get(url).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        this.setCachedGeoJson(url, data);
        this.provincesGeoJSONLayer = L.geoJSON(data, {
          style: {
            color: 'white',
            weight: 1,
            fillColor: 'transparent',
            fillOpacity: 1
          },
          onEachFeature: this.onEachFeatureProvinces.bind(this)
        }).addTo(this.map);
      },
      error: (err) => {
        console.error('Error loading provinces layer:', err);
      }
    });
  }

  loadDistrictLayer(provinceCode: string): void {
    const url = 'assets/maps/geojson/SA_Districts.geojson';
    const cached = this.getCachedGeoJson(url);
    if (cached) {
      this._applyDistrictLayer(cached, provinceCode);
      return;
    }

    this.http.get<any>(url).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.setCachedGeoJson(url, data);
        this._applyDistrictLayer(data, provinceCode);
      },
      error: (err) => {
        console.error('Error loading districts:', err);
      }
    });
  }

  private _applyDistrictLayer(data: any, provinceCode: string): void {
    const filteredDistricts: any = {
      type: 'FeatureCollection',
      features: data.features.filter(
        f => f.properties.PROVINCE === provinceCode
      )
    };

    if (this.districtsGeoJSONLayer) {
      this.map.removeLayer(this.districtsGeoJSONLayer);
    }

    this.districtsGeoJSONLayer = L.geoJSON(filteredDistricts, {
      style: {
        color: 'white',
        weight: 2,
        fillOpacity: 1,
        fillColor: 'transparent'
      },
      onEachFeature: (feature, layer) => {
        const districtName = feature.properties.DISTRICT_N;

        layer.bindTooltip(districtName, {
          permanent: true,
          direction: 'center',
          className: 'district-label'
        });

        layer.on('mouseover', () => {
          const tooltip = layer.getTooltip().getElement();
          tooltip.classList.add('hovered');
          (layer as L.Path).setStyle({ fillColor: '#e8e8e8', fillOpacity: 0.5 });
        });

        layer.on('mouseout', () => {
          const tooltip = layer.getTooltip().getElement();
          tooltip.classList.remove('hovered');
          (layer as L.Path).setStyle({ fillColor: 'transparent', fillOpacity: 1 });
        });

        layer.on({
          click: (e: L.LeafletMouseEvent) => {
            const districtFeature = e.target.feature;
            const districtName = feature.properties.DISTRICT_N;
            const provinceCode = districtFeature.properties.PROVINCE;

            this.districtBounds = e.target.getBounds().pad(0.01);
            this.currentLevel = 2;
            this.lastSelectedDistrictName = districtName;

            this.updateBreadcrumb([
              { label: 'South Africa', level: 1 },
              { label: this.lastSelectedProvinceName, level: 2 },
              { label: districtName, level: 3 }
            ]);

            this.updateZoomLimits(this.currentLevel);

            const bounds = e.target.getBounds().pad(0.01);
            this.map.fitBounds(bounds, SMOOTH_ANIM_OPTIONS);

            if (this.districtsGeoJSONLayer) this.map.removeLayer(this.districtsGeoJSONLayer);
            if (this.map.hasLayer(this.placeholderImage)) this.map.removeLayer(this.placeholderImage);
            if (this.map.hasLayer(this.SABoundaryGeoJsonLayer)) this.map.removeLayer(this.SABoundaryGeoJsonLayer);
            if (this.map.hasLayer(this.provincesGeoJSONLayer)) this.map.removeLayer(this.provincesGeoJSONLayer);

            this.loadregionsGeoJson();
            this.onFilterCheckboxChange();
          }
        });
      }
    }).addTo(this.map);

    this.districtsGeoJSONLayer.eachLayer((layer: any) => {
      const el = layer.getElement?.();
      if (el) {
        el.classList.add('fade-layer-out');
      }
    });

    setTimeout(() => {
      this.districtsGeoJSONLayer.eachLayer((layer: any) => {
        const el = layer.getElement?.();
        if (el) {
          void el.offsetWidth;
          el.classList.add('fade-layer-in');
        }
      });
    }, 10);
  }

  regionStyle(feature?: any): L.PathOptions {
    switch (feature.properties.grey) {
      case "Grey 1":
        return {
          color: "#404040",
          weight: 0.5,
          opacity: 0.7,
          fillColor: "#D4D4D4",
          fillOpacity: 0.7
        };
      case "Grey 2":
        return {
          color: "#404040",
          weight: 0.5,
          opacity: 0.7,
          fillColor: "#BBBBBB",
          fillOpacity: 0.7
        };
      case "Grey 3":
        return {
          color: "#404040",
          weight: 0.5,
          opacity: 0.6,
          fillColor: "#929292",
          fillOpacity: 0.6
        };
      default:
        return {
          color: "#404040",
          weight: 0.5,
          opacity: 0.5,
          fillColor: "#CCCCCC",
          fillOpacity: 0.5
        };
    }
  }

  transparentSaBoundary(feature?: any): L.PathOptions {
    return {
      color: '#f9f9f9',
      weight: 2,
      fillColor: 'transparent',
      fillOpacity: 0
    };
  }

  transparentStyle(feature?: any): L.PathOptions {
    return {
      color: 'transparent',
      weight: 0,
      fillColor: 'transparent',
      fillOpacity: 0
    };
  }

  transparentMetroStyle(feature?: any): L.PathOptions {
    return {
      color: 'transparent',
      weight: 2,
      opacity: 0.5,
      fillColor: 'transparent',
      fillOpacity: 0.2
    };
  }

  transparentProvinceStyle(feature?: any): L.PathOptions {
    return {
      color: 'white',
      weight: 1,
      fillColor: 'transparent',
      fillOpacity: 1
    };
  }

  hideMarkers(markerArray: L.Marker[]): void {
    markerArray.forEach(marker => {
      this.map.removeLayer(marker);
    });
  }

  showMarkers(markerArray: L.Marker[]): void {
    markerArray.forEach(marker => {
      this.map.addLayer(marker);
    });
  }

  defaultPropertyIcon = L.icon({
    iconUrl: '../../assets/images/maps/icons/map-marker.png',
    iconSize: [50, 50],
    iconAnchor: [25, 50]
  });

  enlargedPropertyIcon = L.icon({
    iconUrl: '../../assets/images/maps/icons/map-marker.png',
    iconSize: [75, 75],
    iconAnchor: [37.5, 75]
  });

  selectedMarker: L.Marker | null = null;

  addPropertyAdvertMarkers(adverts: AdvertGeneratorDto[]): void {
    const markers: L.Marker[] = [];

    for (const advert of adverts) {
      const marker = L.marker(
        [Number(advert.latitude), Number(advert.longitude)],
        { icon: this.defaultPropertyIcon }
      );

      marker['propertyData'] = advert;

      const popupContent = document.createElement('div');
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(PropertyForSaleCardComponent);
      const componentRef = this.viewContainerRef.createComponent(componentFactory);
      this.dynamicComponentRefs.push(componentRef);
      componentRef.instance.advert = advert;
      componentRef.instance.marker = marker;
      popupContent.appendChild(componentRef.location.nativeElement);

      marker.bindPopup(popupContent, { closeButton: false, maxWidth: 600 });

      marker.on('click', () => {
        if (this.selectedMarker && this.selectedMarker !== marker) {
          this.selectedMarker.setIcon(this.defaultPropertyIcon);
        }

        const currentIcon = marker.getIcon();
        if (currentIcon === this.defaultPropertyIcon) {
          marker.setIcon(this.enlargedPropertyIcon);
          this.selectedMarker = marker;
        } else {
          marker.setIcon(this.defaultPropertyIcon);
          this.selectedMarker = null;
        }
      });

      marker.on('popupclose', () => {
        marker.setIcon(this.defaultPropertyIcon);
        this.selectedMarker = null;
      });

      markers.push(marker);
    }

    this.propertyMarkers = markers;
    this.showMarkerLayer();
    console.log('[MC] Adding markers:', markers.length, 'clusterGroup:', !!this.markerClusterGroup);
    if (this.markerClusterGroup) {
      this.markerClusterGroup.addLayers(markers);
      console.log('[MC] Cluster layer count after add:', this.markerClusterGroup.getLayers().length);
    } else {
      markers.forEach(m => m.addTo(this.map));
    }
  }

  openSearchPanel(item: MapFilters) {
    const willExpand = item.expanded == false ? true : false;
    this.searchFilters.forEach(filter => filter.expanded = false);
    item.expanded = willExpand;
  }

  gotoOptions() {
    this.pageNumberEvent.emit(2)
  }

  gotoCheckout() {
    this.pageNumberEvent.emit(3)
  }

  getAdvertTypes() {
    this.lookupService.apiV1LookupGetAdvertTypeHierarchyGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: AdvertTypesDto[]) => {
        this.advertTypes = (response || []).map(category => ({
          ...category,
          expanded: false,
          subgroups: (category.subgroups || []).map(sg => ({
            ...sg,
            items: (sg.items || []).map(item => ({ ...item, checked: false }))
          }))
        }));
      },
      error: (_error: any) => {
        this.notificationService.showErrorMessage('Error', 'Could not load advert types.');
      }
    });
  }

  toggleTypeCategory(node: AdvertTypesUi): void {
    node.expanded = !node.expanded;
  }

  collectCheckedTypeItemIds(): string[] {
    const ids: string[] = [];
    for (const category of this.advertTypes) {
      for (const subgroup of category.subgroups || []) {
        for (const item of subgroup.items || []) {
          if (item.checked && item.id) {
            ids.push(item.id);
          }
        }
      }
    }
    return ids;
  }

  showOrHideSearchBlock(zoom: number) {
    // Legacy — search is now always visible at the top
  }

  addLegendControl(): void {
    const legend = (L as any).control({ position: 'topright' });

    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'legend');
      div.style.backgroundColor = 'white';
      div.style.padding = '10px';
      div.style.borderRadius = '10px';
      div.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
      div.style.display = 'flex';
      div.style.flexDirection = 'column';
      div.style.alignItems = 'center';
      div.style.zIndex = '1000';

      const headerWrapper = L.DomUtil.create('div', 'legend-header-wrapper');
      headerWrapper.style.marginBottom = '8px';
      headerWrapper.style.display = 'flex';
      headerWrapper.style.justifyContent = 'center';
      headerWrapper.style.alignItems = 'center';
      headerWrapper.style.width = '40px';
      headerWrapper.style.height = '40px';

      const headerIcon = L.DomUtil.create('span', 'legend-header-icon material-icons');
      headerIcon.innerHTML = 'info';
      headerIcon.style.fontSize = '24px';
      headerIcon.style.color = '#666';

      headerWrapper.appendChild(headerIcon);

      const separator = L.DomUtil.create('div', 'legend-separator');
      separator.style.width = '30px';
      separator.style.height = '1px';
      separator.style.backgroundColor = '#ccc';
      separator.style.marginBottom = '8px';

      div.appendChild(headerWrapper);
      div.appendChild(separator);

      this.legendItems.forEach(item => {
        const iconWrapper = L.DomUtil.create('div', 'legend-icon-wrapper');
        iconWrapper.style.margin = '0';
        iconWrapper.style.position = 'relative';

        const icon = L.DomUtil.create('span', 'legend-span');
        var test = this.createLegendIcon(item.iconPath);

        icon.innerHTML = test;
        icon.style.fontSize = '24px';
        icon.style.cursor = 'pointer';
        icon.style.color = '#666';

        const [title, ...descriptionParts] = item.text.split('\n');
        const description = descriptionParts.join(' ');

        const tooltip = L.DomUtil.create('div', 'legend-tooltip');
        tooltip.style.display = 'none';
        tooltip.style.position = 'absolute';
        tooltip.style.right = '40px';
        tooltip.style.top = '-10px';
        tooltip.style.backgroundColor = 'white';
        tooltip.style.padding = '10px';
        tooltip.style.borderRadius = '10px';
        tooltip.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        tooltip.style.zIndex = '1001';
        tooltip.style.width = '250px';
        tooltip.style.textAlign = 'left';
        tooltip.style.fontFamily = 'Arial, sans-serif';

        const titleElement = L.DomUtil.create('div', 'legend-tooltip-title');
        titleElement.innerHTML = title;
        titleElement.style.fontWeight = 'bold';
        titleElement.style.fontSize = '14px';
        titleElement.style.marginBottom = '5px';

        const descElement = L.DomUtil.create('div', 'legend-tooltip-desc');
        descElement.innerHTML = description;
        descElement.style.fontSize = '12px';
        descElement.style.color = '#666';

        tooltip.appendChild(titleElement);
        tooltip.appendChild(descElement);

        L.DomEvent.on(iconWrapper, 'mouseover', () => {
          tooltip.style.display = 'block';
        });
        L.DomEvent.on(iconWrapper, 'mouseout', () => {
          tooltip.style.display = 'none';
        });

        iconWrapper.appendChild(icon);
        iconWrapper.appendChild(tooltip);
        div.appendChild(iconWrapper);
      });

      return div;
    };

    legend.addTo(this.map);
  }

  private setZoomEnabled(enabled: boolean): void {
    if (!this.map) return;
    if (enabled) {
      this.map.scrollWheelZoom.enable();
      const container = this.map.zoomControl?.getContainer();
      if (container) container.style.display = '';
    } else {
      this.map.scrollWheelZoom.disable();
      const container = this.map.zoomControl?.getContainer();
      if (container) container.style.display = 'none';
    }
  }

  private showMarkerLayer(): void {
    if (this.markerClusterGroup && !this.map.hasLayer(this.markerClusterGroup as any)) {
      this.map.addLayer(this.markerClusterGroup as any);
    }
  }

  private hideMarkerLayer(): void {
    if (this.markerClusterGroup && this.map.hasLayer(this.markerClusterGroup as any)) {
      this.map.removeLayer(this.markerClusterGroup as any);
    }
  }
}

export interface MapFilters {
  id: number,
  name: string,
  subItems: [],
  expanded: boolean
}


export interface AdvertTypeItemUi extends AdvertTypeItemDto {
  checked?: boolean;
}

export interface AdvertTypeSubgroupUi extends AdvertTypeSubgroupDto {
  items?: AdvertTypeItemUi[] | null;
}

export interface AdvertTypesUi extends AdvertTypesDto {
  expanded?: boolean;
  subgroups?: AdvertTypeSubgroupUi[] | null;
}
